import { ChartOptions, TooltipItem } from "chart.js";
import { FC, memo, useMemo } from "react";
import { Line } from "react-chartjs-2";

import { useRtl } from "@ryde/redux";
import { IBarGraphContent } from "@ryde/shared";
import { rydeColorsGraphs } from "@ryde/theme";

import { useDashboardChartPoints } from "../../hooks/use-dashboard-chart-points";
import { DASHBOARD_CHART_BASE_OPTIONS } from "./dashboard-chart.constants";
import {
  dashboardCategoryAxis,
  dashboardValueAxis,
  defaultChartValueFormatter,
} from "./dashboard-chart.utils";
import { LineChartConstants } from "./dashboard-line.chart.constants";

interface IDashboardLineChartProps {
  content: IBarGraphContent;
  metricLabel: string;
  dimensionLabel: string;
  /** Formats the value in labels/tooltip; defaults to a localized number. */
  valueFormatter?: (value: number) => string;
}

/**
 * Dashboard line chart: the dimension runs along the X-axis (its natural
 * sequence) and the metric up the Y-axis, in the brand purple with the value
 * labelled above each point. No legend (single series).
 */
export const DashboardLineChart: FC<IDashboardLineChartProps> = memo(
  ({ content, metricLabel, dimensionLabel, valueFormatter }) => {
    const { isRtl } = useRtl();
    const { labels, values } = useDashboardChartPoints(content);
    const formatValue = useMemo(
      () => valueFormatter ?? defaultChartValueFormatter,
      [valueFormatter],
    );

    const data = useMemo(
      () => ({
        labels,
        datasets: [
          {
            label: metricLabel,
            data: values,
            borderColor: rydeColorsGraphs.verticalBarDefaultColor,
            backgroundColor: rydeColorsGraphs.verticalBarDefaultColor,
            pointBackgroundColor: rydeColorsGraphs.verticalBarDefaultColor,
            pointRadius: LineChartConstants.pointRadius,
            borderWidth: LineChartConstants.borderWidth,
            tension: LineChartConstants.lineTension,
            fill: false,
          },
        ],
      }),
      [labels, values, metricLabel],
    );

    const options: ChartOptions<"line"> = useMemo(
      () => ({
        ...DASHBOARD_CHART_BASE_OPTIONS,
        layout: { padding: { top: 22 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            rtl: isRtl,
            callbacks: {
              label: (item: TooltipItem<"line">) =>
                `${metricLabel}: ${formatValue(Number(item.raw))}`,
            },
          },
          datalabels: {
            align: "top",
            offset: 6,
            color: rydeColorsGraphs.barLabelTextColor,
            font: { size: 11, weight: 600 },
            formatter: (value: number) => formatValue(value),
          },
        },
        scales: {
          x: dashboardCategoryAxis(dimensionLabel, { isRtl }),
          y: dashboardValueAxis(metricLabel, { isRtl }),
        },
      }),
      [metricLabel, dimensionLabel, isRtl, formatValue],
    );

    return <Line data={data} options={options} />;
  },
);

DashboardLineChart.displayName = "DashboardLineChart";
